/**
 * StoryGate — enforces a story's `access` setting (see stories.js) on the route.
 *
 * PUBLIC stories: renders the reel immediately and fires NO request. A public
 * story is therefore byte-identical to an ungated one — no auth round-trip, no
 * loading flash, no behaviour difference between a signed-in and signed-out
 * visitor. That matters because these are the links that get shared around.
 *
 * GATED stories: checks the session first. Signed in → the reel. Signed out →
 * the login screen, having recorded the story so SignIn can bring them straight
 * back to it. Applies on desktop and mobile alike.
 *
 * A 500 or a dropped connection resolves OPEN, not blocked. That matches the
 * app's existing convention (see mobile/__tests__/authGate.test.js): only a real
 * 401/403 means "logged out", and a flaky network must never lock out someone
 * who is in fact signed in. It also means this is a UX gate, not a security
 * boundary — see the note in stories.js.
 */
import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { ACCESS, getStory } from "./stories";
import { rememberStoryEntry } from "../utils/storyEntry";

export default function StoryGate({ slug, children }) {
  // An unknown slug is treated as gated — a story wired into App.js but missing
  // from the registry fails to the safe side rather than silently going public.
  const gated = getStory(slug)?.access !== ACCESS.PUBLIC;

  // View tracking: one fire-and-forget ping per story per browser session
  // (so back-and-forth navigation doesn't inflate counts). Never blocks or
  // affects rendering; failures are silently ignored.
  useEffect(() => {
    try {
      const key = `storyViewed:${slug}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch { /* no sessionStorage: fall through and count the view anyway */ }
    fetch("/api/story-view/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ slug }),
    }).catch(() => {});
  }, [slug]);

  // "open" from the outset for public stories, so they never render a spinner.
  const [status, setStatus] = useState(gated ? "checking" : "open");

  useEffect(() => {
    if (!gated) return undefined;
    let cancelled = false;
    fetch("/api/user-info/", { credentials: "include" })
      .then((r) => {
        if (cancelled) return;
        if (r.ok) { setStatus("open"); return; }
        if (r.status === 401 || r.status === 403) {
          rememberStoryEntry(window.location.pathname);
          setStatus("blocked");
          return;
        }
        setStatus("open"); // server error → fail open
      })
      .catch(() => { if (!cancelled) setStatus("open"); }); // network error → fail open
    return () => { cancelled = true; };
  }, [gated]);

  if (status === "blocked") return <Navigate to="/" replace />;
  // Plain white rather than a spinner: the reels open on a white canvas, so the
  // check reads as the page still painting instead of an extra loading step.
  if (status === "checking") return <div style={{ background: "#fff", height: "100vh" }} />;
  return children;
}
